'use client';

import _ from 'lodash';
import { useRouter } from 'next/navigation';
import { createContext, useContext, useEffect, useState } from 'react';

import { DEVICE_SURFACE, ROLE } from 'config/common';
import getUserDetails from 'helpers/getUserDetails';
import { useIsMobile } from 'helpers/screenResolutionHelper';
import {
  addItemToCart,
  addItemToDefaultCart,
  authenticateCart,
  getCartDetails,
  getSelectedCart,
  getUserEventCarts,
  setSelectedCart
} from 'services/cart.service';
import { OPS_PORTAL_URL } from 'services/connections.service';
import createGuestUserAPI from 'services/guestUser.service';
import { getPageURL, ROUTE_CONFIG } from 'services/hostPortal.service';
import {
  getAgentAuth,
  getAgentInfo,
  getAuthIdentity,
  getUserAuth,
  removeUserAuth,
  setUserAuth
} from 'services/identity.service';
import {
  getSelectedUserEvent,
  setSelectedUserEvent
} from 'services/myEvents.service';
import { getProductById } from 'services/product.service';
import { updateUserTimeZone } from 'services/user.service';

const UIContext = createContext();

export const useUIContext = () => {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error('useUIContext must be used within a UIProvider');
  }
  return context;
};

const defaultCartParameter = {
  cartItems: [],
  listedPriceAmount: 0,
  orderTotal: 0,
  deliveryCharge: 0,
  discountAmount: 0,
  listedDiscountAmount: 0,
  promoCodeDiscountAmount: 0,
  vatAmount: 0,
  promoCodeDetails: null,
  activeDefaultCart: null,
  productPartners: {},
  externalNotes: null,
  internalNotes: null,
  // TODO: Remove the following superseded value.
  areAllProductsAvailable: true,
  cartOrderScreenType: 'cart'
};

const { HOST, PLANNER } = ROLE;
const { MOBILE, WEB } = DEVICE_SURFACE;

const UIProvider = (props) => {
  const [cartList, setCartList] = useState([]);
  const [defaultHostCartDetail, setDefaultHostCartDetail] = useState(null);

  const [defaultCart, setDefaultCart] = useState({});
  const [cart, setCart] = useState(defaultCartParameter);

  const [isLoading, setIsLoading] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [productPaginationEndIndex, setProductPaginationEndIndex] = useState(0);
  const [productCategoryUrl, setProductCategoryUrl] = useState('');
  const router = useRouter();
  const [isMobile] = useIsMobile();
  // TODO: Client side functions should be called separately, for clarity.
  const [agentInfo, setAgentInfo] = useState({});
  const role = agentInfo.agent ? PLANNER : HOST;
  const surface = isMobile ? MOBILE : WEB;
  const [authUser, setAuthUser] = useState({});
  const [showQuoteRequestSubmittedAlert, setShowQuoteRequestSubmittedAlert] =
    useState(false);
  const [isHostIdentityAvailable, setIsHostIdentityAvailable] = useState(false);

  const createGuestUser = async () => {
    const guestUser = await createGuestUserAPI();
    if (guestUser.status) {
      setUserAuth({
        id: guestUser?.entity?.id,
        isGuest: true
      });
      setIsHostIdentityAvailable(true);
      setSelectedUserEvent({ data: null });
    }
  };

  const updateCartInPortalHeader = (patch) => {
    const updatedCart = _.assign(cart, patch);
    setCart({
      ...updatedCart,
      cartItemCount: updatedCart.cartItems?.length ?? 0
    });
  };

  const getCartDetailsToUpdate = (cartDetails) => ({
    ...cartDetails,
    promoCodeDetails: cartDetails.derivedValues?.promoCode,
    promoCodeDiscountAmount: cartDetails.derivedValues?.promoCodeDiscountAmount,
    promoCodeValidationInfo: cartDetails.derivedValues?.promoCodeValidationInfo
  });

  const resetSelectedCart = () => {
    setSelectedCart({ data: null });
  };

  const getUserEventCartList = async () => {
    const { id: userCartId } = getAuthIdentity();
    const { entity: userEventCarts = [] } = await getUserEventCarts(
      userCartId,
      getSelectedUserEvent()
    );
    userEventCarts.length > 0 && setCartList(userEventCarts);
    return userEventCarts;
  };

  const getAvailableCartDetail = async () => {
    const { id: userCartId } = getAuthIdentity();
    const userEventCartList = await getUserEventCartList();
    if (userEventCartList.length > 0) {
      const availableDefaultCart = userEventCartList.find(
        ({ isDefault }) => isDefault
      );
      const cartId = getSelectedCart()?.id || availableDefaultCart?.id;
      if (cartId) {
        const { entity: localCart } = await getCartDetails({
          userCartId,
          cartId
        });
        if (localCart) {
          setDefaultCart(userEventCartList, availableDefaultCart);
          setDefaultHostCartDetail(localCart);
          updateCartInPortalHeader(
            getCartDetailsToUpdate({
              ...localCart,
              userCartId
            })
          );
        }
      }
      setIsLoading(false);
    }
  };

  const checkAuth = async () => {
    let auth = getAuthIdentity();
    if (!auth) {
      auth = await createGuestUser();
    }
    setIsHostIdentityAvailable(true);
    getAvailableCartDetail();
  };
  const getAuthenticatedUserDetail = () => {
    const auth = getAuthIdentity();
    return getUserDetails(auth?.id) || {};
  };

  const getAndSetAuthUser = async () => {
    const hostInfo = await getAuthenticatedUserDetail();
    if (hostInfo) {
      setAuthUser(hostInfo);
    }
  };
  const updateSelectedCart = (patch) => {
    setSelectedCart({
      data: patch
    });
  };

  useEffect(() => {
    getAndSetAuthUser();
  }, []);

  useEffect(() => {
    getSelectedUserEvent() && getUserEventCartList();
  }, [getSelectedUserEvent()]);

  useEffect(() => {
    checkAuth();
    setAgentInfo(getAgentInfo());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createCart = async ({ product, quantity, revertQuantityToMin }) => {
    const userCartId = getAuthIdentity().id;
    const addToCartApi = getSelectedCart()?.id
      ? addItemToCart
      : addItemToDefaultCart;
    const res = await addToCartApi(
      userCartId,
      {
        userCartId,
        entityId: product.id,
        entityType: product.type,
        quantity,
        priceId: product.price.id
      },
      getSelectedCart()?.id
    );
    if (res.status) {
      revertQuantityToMin();
      getAvailableCartDetail();
    }
    return res.entity;
  };

  useEffect(() => {
    // TODO: Tanmay to include cartId to make it more usable for third party
    window.addToCart = async (productId, quantity) => {
      const userCartId = getAuthIdentity().id;
      const { entity: product } = await getProductById(productId);
      const addToCartApi = getSelectedCart()?.id
        ? addItemToCart
        : addItemToDefaultCart;
      const res = await addToCartApi(
        userCartId,
        {
          userCartId,
          entityId: productId,
          entityType: product.type,
          quantity,
          priceId: product.price.id
        },
        getSelectedCart()?.id
      );
      if (res.status) {
        getAvailableCartDetail();
      }
    };
  }, []);

  const navigateUser = (pathName) => {
    const defaultLocation = getPageURL({
      pageName: ROUTE_CONFIG.HOME_PAGE.label
    });
    const location = pathName || defaultLocation;

    return router.push(location);
  };

  const updateAndSetUserTimeZone = async () => {
    const userAuth = getUserAuth() || {};

    const updatedUserTimeZone = await updateUserTimeZone(userAuth.id, {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    if (updatedUserTimeZone.status) {
      setUserAuth({
        ...userAuth,
        timeZone: updatedUserTimeZone.entity.timeZone
      });
    }
  };

  const logIn = async (userDetails, pathName, isCheckout) => {
    const planner = getAgentAuth();
    const silentlyRedirectIfHostPortalOTPLoginIsAttemptedByAgent =
      planner?.token;
    if (silentlyRedirectIfHostPortalOTPLoginIsAttemptedByAgent) {
      router.replace(OPS_PORTAL_URL);
    }

    const guestUser = getUserAuth();
    const response = await authenticateCart(userDetails.id, guestUser?.id);
    if (response.status) {
      setUserAuth(userDetails);
      setIsHostIdentityAvailable(true);
      updateAndSetUserTimeZone();
      resetSelectedCart();
      getAvailableCartDetail();
      getAndSetAuthUser();
      !isCheckout && navigateUser(pathName);
      return true;
    }
    const loginQueryParam = pathName
      ? {
          redirectTo: pathName
        }
      : {};
    router.push(
      getPageURL({
        pageName: ROUTE_CONFIG.LOGIN.label,
        queryParams: loginQueryParam
      })
    );

    return false;
  };

  const hostLogOut = () => {
    removeUserAuth();
    resetSelectedCart();
    setSelectedUserEvent({ data: null });

    return router.push(
      getPageURL({
        pageName: ROUTE_CONFIG.LOGIN.label
      })
    );
  };

  const updateScrollPosition = (scrollValue) => {
    setScrollPosition(scrollValue);
  };

  const updateProductPaginationEndIndex = (end) => {
    setProductPaginationEndIndex(end);
  };

  const updateCategoryUrl = (url) => {
    setProductCategoryUrl(url);
  };

  const value = {
    ...cart,
    agentInfo,
    authUser,
    cartList,
    createCart,
    defaultCart,
    defaultHostCartDetail,
    getUserEventCartList,
    isHostIdentityAvailable,
    isLoading,
    logIn,
    logOut: hostLogOut,
    productCategoryUrl,
    productPaginationEndIndex,
    resetSelectedCart,
    role,
    scrollPosition,
    selectedCart: getSelectedCart(),
    setDefaultHostCartDetail,
    setShowQuoteRequestSubmittedAlert,
    showQuoteRequestSubmittedAlert,
    surface,
    updateAndSetUserTimeZone,
    updateCartInPortalHeader,
    updateCategoryUrl,
    updateProductPaginationEndIndex,
    updateScrollPosition,
    updateSelectedCart,
    userCartId: agentInfo?.id
  };

  return (
    <UIContext.Provider
      value={value}
      {...props}
    />
  );
};

export const ManagedUIContext = ({ children }) => (
  <UIProvider>{children}</UIProvider>
);
